import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';

/**
 * Operator-only cost + abuse dashboard. Server gates on isAdminEmail
 * (gas@gasweb.info / talibkundalini@gmail.com by default), so loading
 * this page from a non-admin account just produces 403s on every fetch.
 *
 * Surface:
 *   - 30-day totals across all households (revenue, cost, margin, GB)
 *   - Per-household table (name, status, GB, cost, revenue)
 *   - Open sharing alerts with a "Resolve" button
 */
export default function AdminDashboard() {
  const navigate = useNavigate();
  type CostSummaryData = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminCostSummary>>['data']>;
  type AlertList = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminListSharingAlerts>>['data']>;
  const [costData, setCostData] = useState<CostSummaryData | null>(null);
  const [alerts, setAlerts] = useState<AlertList>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const refresh = async () => {
    setLoading(true);
    const [cost, alertsRes] = await Promise.all([
      streamFlowApi.adminCostSummary(days),
      streamFlowApi.adminListSharingAlerts(),
    ]);
    if (cost.success && cost.data) setCostData(cost.data);
    else if (cost.error) toast.error(cost.error.message);
    if (alertsRes.success && alertsRes.data) setAlerts(alertsRes.data);
    setLoading(false);
  };

  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  const onResolve = async (id: string) => {
    const note = window.prompt('Resolution notes (optional)') ?? undefined;
    const res = await streamFlowApi.adminResolveSharingAlert(id, note);
    if (res.success) {
      toast.success('Alert resolved');
      void refresh();
    } else {
      toast.error(res.error?.message ?? 'Failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Operator Dashboard</h1>
          <p className="text-sm text-gray-500">Cost attribution + abuse signals across all households</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={() => navigate('/admin/streaming')} className="text-sm text-blue-600 hover:underline">
            Live Streams ▸
          </button>
          <button onClick={() => navigate('/')} className="text-sm text-blue-600 hover:underline">
            ← Dashboard
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      {costData && (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Households" value={String(costData.totals.households)} />
            <Stat label="GB out" value={costData.totals.gb.toFixed(1)} />
            <Stat label="Egress cost" value={`$${costData.totals.cost_usd.toFixed(2)}`} />
            <Stat
              label="Revenue (MRR)"
              value={`$${costData.totals.revenue_usd.toFixed(2)}`}
              hint={`Margin $${(costData.totals.revenue_usd - costData.totals.cost_usd).toFixed(2)}`}
            />
          </div>

          {/* Per-household table */}
          <div className="rounded-lg border border-gray-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Household</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">GB</th>
                  <th className="px-3 py-2 font-medium text-right">Cost</th>
                  <th className="px-3 py-2 font-medium text-right">Revenue</th>
                  <th className="px-3 py-2 font-medium text-right">Margin</th>
                  <th className="px-3 py-2 font-medium text-right">Requests</th>
                </tr>
              </thead>
              <tbody>
                {costData.households.map((h) => (
                  <tr key={h.household_id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{h.household_name}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] uppercase ${statusBg(h.subscription_status)}`}>
                        {h.subscription_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.gb.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">${h.cost_usd.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">${h.monthly_revenue_usd.toFixed(2)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${h.monthly_revenue_usd - h.cost_usd < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                      ${(h.monthly_revenue_usd - h.cost_usd).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.requests}</td>
                  </tr>
                ))}
                {costData.households.length === 0 && (
                  <tr><td className="px-3 py-6 text-center text-gray-500" colSpan={7}>No bandwidth data yet for this window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Sharing alerts */}
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h2 className="font-semibold">Open sharing alerts</h2>
          <span className="text-xs text-gray-500">{alerts.length} open</span>
        </div>
        {alerts.length === 0 && <div className="px-3 py-6 text-center text-sm text-gray-500">No open alerts. Nice.</div>}
        {alerts.map((a) => (
          <div key={a.id} className="border-t border-gray-100 px-3 py-3 flex items-center justify-between gap-3">
            <div>
              <div className="font-medium text-sm">{a.sf_households?.name ?? a.household_id}</div>
              <div className="text-xs text-gray-500">
                {a.distinct_devices} distinct devices in 24h · detected {new Date(a.detected_at).toLocaleString()}
              </div>
            </div>
            <button
              onClick={() => onResolve(a.id)}
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm text-amber-900 hover:bg-amber-100"
            >
              Resolve
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function statusBg(s: string): string {
  switch (s) {
    case 'active': return 'bg-emerald-100 text-emerald-800';
    case 'trialing': return 'bg-blue-100 text-blue-800';
    case 'past_due': return 'bg-amber-100 text-amber-800';
    case 'canceled': return 'bg-rose-100 text-rose-800';
    default: return 'bg-gray-100 text-gray-700';
  }
}
